import { createContext, useContext } from "react";

// Primitive string union aliases — duplicated here so shared/lib does not
// import from @/entities/user. Keep in sync with entities/user/model/types.ts.
export type PermissionAction = "read" | "create" | "update" | "delete" | "refund";

export type PermissionResource =
  | "user"
  | "store"
  | "product"
  | "category"
  | "order"
  | "variant"
  | "customer"
  | "role"
  | "transaction"
  | "return";

// Minimal structural shape of profile data used by the context.
// Must stay in sync with entities/user/model/types.ts but uses only primitives.
export interface PermissionProfileStore {
  id: string;
  name: string;
  role: string;
  status: string;
  theme: string;
  permissions: Record<string, PermissionAction[]>;
}

export interface PermissionProfileUser {
  id: string;
  auth0Id: string;
  email: string;
  name: string;
  picture: string;
  phone?: string | null;
  isSuperAdmin: boolean;
  canChangePassword?: boolean;
}

export interface PermissionProfileData {
  user: PermissionProfileUser;
  store: PermissionProfileStore;
  stores: Array<{ id: string; name: string }>;
}

export interface PermissionContextType {
  profileData: PermissionProfileData | null;
  hasPermission: (resource: PermissionResource, action: PermissionAction) => boolean;
  isSuperAdmin: boolean;
  userRole: string | null;
  storePermissions: Record<string, PermissionAction[]>;
}

export const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export const usePermissionContext = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error("usePermissionContext must be used within a PermissionProvider");
  }
  return context;
};
